import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useBarista } from './BaristaContext';

if (typeof document !== 'undefined' && !document.getElementById('toast-keyframes')) {
    const style = document.createElement('style');
    style.id = 'toast-keyframes';
    style.innerHTML = `
        @keyframes toastSlideIn {
            from { transform: translateX(24px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
        }
        @keyframes toastShrink {
            from { width: 100%; }
            to { width: 0%; }
        }
    `;
    document.head.appendChild(style);
}

const NotificationsContext = createContext(null);

export function useNotifications() {
    const ctx = useContext(NotificationsContext);
    if (!ctx) throw new Error('useNotifications must be used within NotificationsProvider');
    return ctx;
}

const getUserType = () => sessionStorage.getItem('userType') || localStorage.getItem('userType');
const isSuperAdmin = () => getUserType() === 'admin';

export function NotificationsProvider({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const locationRef = useRef(location.pathname);
    useEffect(() => { locationRef.current = location.pathname; }, [location.pathname]);

    const { selectedBarista } = useBarista();

    const [staffBranchRole, setStaffBranchRole] = useState(null);
    const [staffBranchId, setStaffBranchId] = useState(null);
    const [staffAdjustmentRequests, setStaffAdjustmentRequests] = useState([]);
    const [staffAdjustmentResolutions, setStaffAdjustmentResolutions] = useState([]);
    const [staffCountAlerts, setStaffCountAlerts] = useState([]);
    const [staffEODAlerts, setStaffEODAlerts] = useState([]);
    const [staffNewOrders, setStaffNewOrders] = useState([]);
    const [staffRemakeAlerts, setStaffRemakeAlerts] = useState([]);
    const [adminLowStockAlerts, setAdminLowStockAlerts] = useState([]);
    const [toasts, setToasts] = useState([]);
    const pushToast = (toast) => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        setToasts(prev => [...prev, { id, ...toast }].slice(-4));
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 6000);
    };
    const dismissToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

    // Navigates to the staff dashboard (wherever InventoryManagement lives) and
    // tells it which sub-tab to open, whether or not it's currently mounted.
    const navigateToInventory = useCallback((tab) => {
        sessionStorage.setItem('pendingInventoryTab', tab);
        window.dispatchEvent(new CustomEvent('kaperk:navigate-inventory', { detail: { tab } }));
        if (locationRef.current !== '/staff-dashboard') {
            navigate('/staff-dashboard');
        }
    }, [navigate]);

    // Navigates to the staff dashboard's Orders view, whether or not
    // OrderManagement is currently mounted. StaffDashboard should listen
    // for 'kaperk:navigate-orders' the same way it listens for
    // 'kaperk:navigate-inventory'.
   const navigateToOrders = useCallback(() => {
        sessionStorage.setItem('pendingStaffTab', 'orders');
        window.dispatchEvent(new CustomEvent('kaperk:navigate-orders'));
        if (locationRef.current !== '/staff-dashboard') {
            navigate('/staff-dashboard');
        }
    }, [navigate]);

    // Remake details (reason, responsible baristas, resolution status) live in
    // ShiftAttendance's Remake Accountability card, so remake notifications
    // land there rather than in Order Management.
    const navigateToShiftAttendance = useCallback((scrollTarget) => {
        sessionStorage.setItem('pendingStaffTab', 'attendance');
        if (scrollTarget) sessionStorage.setItem('pendingAttendanceScrollTarget', scrollTarget);
        window.dispatchEvent(new CustomEvent('kaperk:navigate-attendance', { detail: { scrollTarget } }));
        if (locationRef.current !== '/staff-dashboard') {
            navigate('/staff-dashboard');
        }
    }, [navigate]);

    // Navigates the Super Admin dashboard to its Menu Management tab,
    // whether or not Dashboard.js is currently mounted.
    const navigateToMenuManagement = useCallback(() => {
        window.dispatchEvent(new CustomEvent('kaperk:navigate-menu-management'));
        if (locationRef.current !== '/dashboard') {
            navigate('/dashboard');
        }
    }, [navigate]);

    const fetchBranchAdjustmentRequests = useCallback(async (branchId, role) => {
        if (isSuperAdmin() || role !== 'Admin' || !branchId) return;
        try {
            const { data, error } = await supabase
                .schema('inventory')
                .from('inventory_adjustment_requests')
                .select(`
                    id, ingredient_id, adjustment_type, quantity, reason, notes,
                    status, requested_by_barista_id, requested_by_staff_id, created_at
                `)
                .eq('branch_id', branchId)
                .eq('status', 'pending')
                .order('created_at', { ascending: false });
            if (error) throw error;

            const ingredientIds = [...new Set((data || []).map(r => r.ingredient_id))];
            const ingredientMap = new Map();
            if (ingredientIds.length > 0) {
                const { data: ingredients } = await supabase
                    .schema('inventory')
                    .from('ingredients')
                    .select('ingredient_id, name, unit')
                    .in('ingredient_id', ingredientIds);
                (ingredients || []).forEach(i => ingredientMap.set(i.ingredient_id, i));
            }

            const baristaIds = [...new Set((data || []).map(r => r.requested_by_barista_id).filter(Boolean))];
            const baristaMap = new Map();
            if (baristaIds.length > 0) {
                const { data: baristas } = await supabase
                    .from('barista_info')
                    .select('barista_id, first_name, last_name')
                    .in('barista_id', baristaIds);
                (baristas || []).forEach(b => baristaMap.set(b.barista_id, `${b.first_name} ${b.last_name}`));
            }

            const dismissedAdj = JSON.parse(localStorage.getItem('dismissedAdjustmentNotifs') || '[]');
            setStaffAdjustmentRequests((data || [])
                .filter(r => !dismissedAdj.includes(r.id))
                .map(r => ({
                    ...r,
                    ingredient_name: ingredientMap.get(r.ingredient_id)?.name || 'Unknown Ingredient',
                    ingredient_unit: ingredientMap.get(r.ingredient_id)?.unit || '',
                    requested_by: r.requested_by_barista_id
                        ? baristaMap.get(r.requested_by_barista_id) || 'Unknown Barista'
                        : 'Staff',
                })));
        } catch (err) {
            console.error('Error fetching adjustment requests for notif bell:', err);
        }
    }, []);

    const fetchStockCountAlerts = useCallback(async (branchId, role) => {
        if (isSuperAdmin() || role !== 'Admin' || !branchId) return;
        try {
            const today = new Date().toISOString().split('T')[0];
            const dayStart = `${today}T00:00:00+00:00`;
            const dayEnd = `${today}T23:59:59+00:00`;

            const { data, error } = await supabase
                .schema('inventory')
                .from('staff_counts')
                .select(`
                    staff_count_id, ingredient_id, counted_quantity, discrepancy,
                    count_type, created_at, counted_by_barista_id
                `)
                .eq('branch_id', branchId)
                .not('counted_by_barista_id', 'is', null)
                .gte('created_at', dayStart)
                .lte('created_at', dayEnd)
                .order('created_at', { ascending: false });
            if (error) throw error;

            const dismissed = JSON.parse(localStorage.getItem('dismissedStockCountAlerts') || '[]');
            const unread = (data || []).filter(r => !dismissed.includes(r.staff_count_id));

            const ingredientIds = [...new Set(unread.map(r => r.ingredient_id))];
            const ingredientMap = new Map();
            if (ingredientIds.length > 0) {
                const { data: ingredients } = await supabase
                    .schema('inventory')
                    .from('ingredients')
                    .select('ingredient_id, name, unit')
                    .in('ingredient_id', ingredientIds);
                (ingredients || []).forEach(i => ingredientMap.set(i.ingredient_id, i));
            }

            const baristaIds = [...new Set(unread.map(r => r.counted_by_barista_id).filter(Boolean))];
            const baristaMap = new Map();
            if (baristaIds.length > 0) {
                const { data: baristas } = await supabase
                    .from('barista_info')
                    .select('barista_id, first_name, last_name')
                    .in('barista_id', baristaIds);
                (baristas || []).forEach(b => baristaMap.set(b.barista_id, `${b.first_name} ${b.last_name}`));
            }

            setStaffCountAlerts(unread.map(r => ({
                ...r,
                ingredient_name: ingredientMap.get(r.ingredient_id)?.name || 'Unknown Ingredient',
                ingredient_unit: ingredientMap.get(r.ingredient_id)?.unit || '',
                counted_by: baristaMap.get(r.counted_by_barista_id) || 'Barista',
            })));
        } catch (err) {
            console.error('Error fetching stock count alerts:', err);
        }
    }, []);

    const fetchEODAlerts = useCallback(async (branchId, role) => {
        if (isSuperAdmin() || role !== 'Admin' || !branchId) return;
        try {
            const dismissed = JSON.parse(localStorage.getItem('dismissedEODAlerts') || '[]');
            const { data, error } = await supabase
                .from('eod_report_log')
                .select('id, branch_id, branch_name, sent_to, report_date, status, error_message, created_at')
                .eq('branch_id', branchId)
                .order('created_at', { ascending: false })
                .limit(10);
            if (error) throw error;

            setStaffEODAlerts((data || []).filter(r => !dismissed.includes(r.id)));
        } catch (err) {
            console.error('Error fetching EOD report alerts:', err);
        }
    }, []);

    const fetchNewOrders = useCallback(async (branchId, role) => {
        if (isSuperAdmin() || role !== 'Barista' || !branchId) return;
        try {
            const { data, error } = await supabase
                .from('orders')
                .select('order_id, reference_number, branch_id, order_source, fulfillment_type, order_status, final_amount, created_at')
                .eq('branch_id', branchId)
                .eq('order_status', 'pending')
                .order('created_at', { ascending: false });
            if (error) throw error;

            const dismissed = JSON.parse(localStorage.getItem('dismissedNewOrderNotifs') || '[]');
            setStaffNewOrders((data || []).filter(o => !dismissed.includes(o.order_id)));
       } catch (err) {
            console.error('Error fetching new orders for notif bell:', err);
        }
    }, []);

    // Admin-side view of remake requests raised by baristas in this branch.
    const fetchRemakeAlerts = useCallback(async (branchId, role) => {
        if (isSuperAdmin() || role !== 'Admin' || !branchId) return;
        try {
            const { data, error } = await supabase
                .from('remake_requests')
                .select('remake_request_id, order_id, reference_number, reason, responsible_staff, submitted_by_barista, status, created_at')
                .eq('branch_id', branchId)
                .order('created_at', { ascending: false })
                .limit(20);
            if (error) throw error;

            const dismissed = JSON.parse(localStorage.getItem('dismissedRemakeAlerts') || '[]');
            setStaffRemakeAlerts((data || []).filter(r => !dismissed.includes(r.remake_request_id)));
        } catch (err) {
            console.error('Error fetching remake alerts for notif bell:', err);
        }
    }, []);

    const fetchLowStockAlerts = useCallback(async (branchId, role) => {
        if (isSuperAdmin() || role !== 'Admin' || !branchId) return;
        try {
            const { data, error } = await supabase
                .schema('inventory')
                .from('branch_inventory')
                .select('id, ingredient_id, current_stock, reorder_level, last_updated')
                .eq('branch_id', branchId);
            if (error) throw error;

            const ingredientIds = [...new Set((data || []).map(r => r.ingredient_id))];
            const ingredientMap = new Map();
            if (ingredientIds.length > 0) {
                const { data: ingredients, error: ingredientsError } = await supabase
                    .schema('inventory')
                    .from('ingredients')
                    .select('ingredient_id, name, unit, category')
                    .in('ingredient_id', ingredientIds);
                if (ingredientsError) {
                    console.error('Error fetching ingredients for low stock alerts (check RLS on inventory.ingredients):', ingredientsError);
                }
                // Normalize keys to strings so number/uuid/string id mismatches don't break the lookup
                (ingredients || []).forEach(i => ingredientMap.set(String(i.ingredient_id), i));
            }

            const dismissed = JSON.parse(localStorage.getItem('dismissedLowStockAlerts') || '[]');
            const lowItems = (data || [])
                .map(r => ({
                    ...r,
                    available_stock: Number(r.current_stock || 0),
                    ingredient_name: ingredientMap.get(String(r.ingredient_id))?.name || 'Unknown Ingredient',
                    ingredient_unit: ingredientMap.get(String(r.ingredient_id))?.unit || '',
                    ingredient_category: ingredientMap.get(String(r.ingredient_id))?.category || '',
                }))
                .filter(r => r.available_stock <= r.reorder_level && !dismissed.includes(r.id));

            setAdminLowStockAlerts(lowItems);
        } catch (err) {
            console.error('Error fetching low stock alerts for notif bell:', err);
        }
    }, []);

    const fetchAdjustmentResolutions = useCallback(async (branchId, role, baristaId) => {
        if (isSuperAdmin() || role !== 'Barista' || !branchId || !baristaId) return;
        try {
            const { data, error } = await supabase
                .schema('inventory')
                .from('inventory_adjustment_requests')
                .select(`
                    id, ingredient_id, adjustment_type, quantity, reason, notes,
                    status, requested_by_barista_id, created_at
                `)
                .eq('branch_id', branchId)
                .eq('requested_by_barista_id', baristaId)
                .in('status', ['approved', 'rejected'])
                .order('created_at', { ascending: false })
                .limit(10);
            if (error) throw error;

            const dismissed = JSON.parse(localStorage.getItem('dismissedAdjustmentResolutions') || '[]');
            const unread = (data || []).filter(r => !dismissed.includes(r.id));

            const ingredientIds = [...new Set(unread.map(r => r.ingredient_id))];
            const ingredientMap = new Map();
            if (ingredientIds.length > 0) {
                const { data: ingredients } = await supabase
                    .schema('inventory')
                    .from('ingredients')
                    .select('ingredient_id, name, unit')
                    .in('ingredient_id', ingredientIds);
                (ingredients || []).forEach(i => ingredientMap.set(i.ingredient_id, i));
            }

            setStaffAdjustmentResolutions(unread.map(r => ({
                ...r,
                ingredient_name: ingredientMap.get(r.ingredient_id)?.name || 'Unknown Ingredient',
                ingredient_unit: ingredientMap.get(r.ingredient_id)?.unit || '',
            })));
        } catch (err) {
            console.error('Error fetching adjustment request resolutions:', err);
        }
    }, []);

    const dismissStockCountAlert = (id) => {
        const dismissed = JSON.parse(localStorage.getItem('dismissedStockCountAlerts') || '[]');
        if (!dismissed.includes(id)) dismissed.push(id);
        localStorage.setItem('dismissedStockCountAlerts', JSON.stringify(dismissed));
        setStaffCountAlerts(prev => prev.filter(n => n.staff_count_id !== id));
    };
    const dismissAdjustmentNotif = (id) => {
        const dismissed = JSON.parse(localStorage.getItem('dismissedAdjustmentNotifs') || '[]');
        if (!dismissed.includes(id)) dismissed.push(id);
        localStorage.setItem('dismissedAdjustmentNotifs', JSON.stringify(dismissed));
        setStaffAdjustmentRequests(prev => prev.filter(n => n.id !== id));
    };

    const dismissAdjustmentResolution = (id) => {
        const dismissed = JSON.parse(localStorage.getItem('dismissedAdjustmentResolutions') || '[]');
        if (!dismissed.includes(id)) dismissed.push(id);
        localStorage.setItem('dismissedAdjustmentResolutions', JSON.stringify(dismissed));
        setStaffAdjustmentResolutions(prev => prev.filter(n => n.id !== id));
    };

    const clearAllStockCountAlerts = () => {
        const ids = staffCountAlerts.map(a => a.staff_count_id);
        const dismissed = JSON.parse(localStorage.getItem('dismissedStockCountAlerts') || '[]');
        localStorage.setItem('dismissedStockCountAlerts', JSON.stringify([...dismissed, ...ids]));
        setStaffCountAlerts([]);
    };

    const clearAllAdjustmentRequests = () => {
        const ids = staffAdjustmentRequests.map(a => a.id);
        const dismissed = JSON.parse(localStorage.getItem('dismissedAdjustmentNotifs') || '[]');
        localStorage.setItem('dismissedAdjustmentNotifs', JSON.stringify([...dismissed, ...ids]));
        setStaffAdjustmentRequests([]);
    };

    const clearAllAdjustmentResolutions = () => {
        const ids = staffAdjustmentResolutions.map(a => a.id);
        const dismissed = JSON.parse(localStorage.getItem('dismissedAdjustmentResolutions') || '[]');
        localStorage.setItem('dismissedAdjustmentResolutions', JSON.stringify([...dismissed, ...ids]));
        setStaffAdjustmentResolutions([]);
    };

    const dismissEODAlert = (id) => {
        const dismissed = JSON.parse(localStorage.getItem('dismissedEODAlerts') || '[]');
        if (!dismissed.includes(id)) dismissed.push(id);
        localStorage.setItem('dismissedEODAlerts', JSON.stringify(dismissed));
        setStaffEODAlerts(prev => prev.filter(n => n.id !== id));
    };

    const clearAllEODAlerts = () => {
        const ids = staffEODAlerts.map(a => a.id);
        const dismissed = JSON.parse(localStorage.getItem('dismissedEODAlerts') || '[]');
        localStorage.setItem('dismissedEODAlerts', JSON.stringify([...dismissed, ...ids]));
        setStaffEODAlerts([]);
    };

    const dismissNewOrderNotif = (id) => {
        const dismissed = JSON.parse(localStorage.getItem('dismissedNewOrderNotifs') || '[]');
        if (!dismissed.includes(id)) dismissed.push(id);
        localStorage.setItem('dismissedNewOrderNotifs', JSON.stringify(dismissed));
        setStaffNewOrders(prev => prev.filter(n => n.order_id !== id));
    };

    const clearAllNewOrderNotifs = () => {
        const ids = staffNewOrders.map(o => o.order_id);
        const dismissed = JSON.parse(localStorage.getItem('dismissedNewOrderNotifs') || '[]');
        localStorage.setItem('dismissedNewOrderNotifs', JSON.stringify([...dismissed, ...ids]));
        setStaffNewOrders([]);
    };

    const dismissRemakeAlert = (id) => {
        const dismissed = JSON.parse(localStorage.getItem('dismissedRemakeAlerts') || '[]');
        if (!dismissed.includes(id)) dismissed.push(id);
        localStorage.setItem('dismissedRemakeAlerts', JSON.stringify(dismissed));
        setStaffRemakeAlerts(prev => prev.filter(n => n.remake_request_id !== id));
    };

    const clearAllRemakeAlerts = () => {
        const ids = staffRemakeAlerts.map(r => r.remake_request_id);
        const dismissed = JSON.parse(localStorage.getItem('dismissedRemakeAlerts') || '[]');
        localStorage.setItem('dismissedRemakeAlerts', JSON.stringify([...dismissed, ...ids]));
        setStaffRemakeAlerts([]);
    };

    const dismissLowStockAlert = (id) => {
        const dismissed = JSON.parse(localStorage.getItem('dismissedLowStockAlerts') || '[]');
        if (!dismissed.includes(id)) dismissed.push(id);
        localStorage.setItem('dismissedLowStockAlerts', JSON.stringify(dismissed));
        setAdminLowStockAlerts(prev => prev.filter(n => n.id !== id));
    };

    const clearAllLowStockAlerts = () => {
        const ids = adminLowStockAlerts.map(a => a.id);
        const dismissed = JSON.parse(localStorage.getItem('dismissedLowStockAlerts') || '[]');
        localStorage.setItem('dismissedLowStockAlerts', JSON.stringify([...dismissed, ...ids]));
        setAdminLowStockAlerts([]);
    };

    // Resolve the logged-in staff member's branch/role once
    useEffect(() => {
        if (isSuperAdmin()) return;
        (async () => {
            try {
                const userEmail = sessionStorage.getItem('userEmail') || localStorage.getItem('userEmail');
                if (!userEmail) return;
                const { data: staffData, error } = await supabase
                    .from('staff')
                    .select('role, branch_id')
                    .or(`email.eq.${userEmail},company_email.eq.${userEmail}`)
                    .maybeSingle();
                if (error || !staffData) return;
                setStaffBranchRole(staffData.role || null);
                setStaffBranchId(staffData.branch_id || null);
            } catch (err) {
                console.error('Error loading staff branch info for notifications:', err);
            }
        })();
    }, []);

    // App-wide realtime subscription — lives here, not in Header, so it survives navigation
    useEffect(() => {
        if (isSuperAdmin() || !staffBranchId || (staffBranchRole !== 'Admin' && staffBranchRole !== 'Barista')) return;

        fetchBranchAdjustmentRequests(staffBranchId, staffBranchRole);
        fetchStockCountAlerts(staffBranchId, staffBranchRole);
        fetchEODAlerts(staffBranchId, staffBranchRole);
        fetchNewOrders(staffBranchId, staffBranchRole);
        fetchLowStockAlerts(staffBranchId, staffBranchRole);
        fetchAdjustmentResolutions(staffBranchId, staffBranchRole, selectedBarista?.barista_id);
        fetchRemakeAlerts(staffBranchId, staffBranchRole);

        const channel = supabase
            .channel(`branch-notifs-${staffBranchId}`)
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'orders', filter: `branch_id=eq.${staffBranchId}` },
                (payload) => {
                    fetchNewOrders(staffBranchId, staffBranchRole);
                    if (staffBranchRole !== 'Barista') return;
                    const row = payload.new;
                    pushToast({
                        type: 'neworder',
                        title: `New order #${row.reference_number || row.order_id}`,
                        message: `${row.order_source === 'mobile_app' ? 'Mobile Customer' : 'Walk-in'} · ${(row.fulfillment_type || '').replace('_', '-')} · ₱${parseFloat(row.final_amount || 0).toFixed(2)}`,
                        onClick: () => navigateToOrders(),
                    });
                }
            )
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'inventory', table: 'branch_inventory', filter: `branch_id=eq.${staffBranchId}` },
                async (payload) => {
                    fetchLowStockAlerts(staffBranchId, staffBranchRole);
                    if (staffBranchRole !== 'Admin') return;
                    const row = payload.new;
                    const available = Number(row.current_stock || 0);
                    const reorder = Number(row.reorder_level || 0);
                    // Note: payload.old only has full column values if the table has
                    // REPLICA IDENTITY FULL set — otherwise this falls back to "always toast when low".
                    const oldRow = payload.old;
                    const wasAvailable = oldRow
                        ? Number(oldRow.current_stock || 0)
                        : null;
                    const wasLow = wasAvailable !== null ? wasAvailable <= reorder : false;
                    if (available <= reorder && !wasLow) {
                        const { data: ingredient } = await supabase
                            .schema('inventory').from('ingredients')
                            .select('name, unit').eq('ingredient_id', row.ingredient_id).single();
                        pushToast({
                            type: 'lowstock',
                            title: available <= 0
                                ? `${ingredient?.name || 'Ingredient'} is out of stock`
                                : `${ingredient?.name || 'Ingredient'} is low on stock`,
                            message: `${available} ${ingredient?.unit || ''} available · reorder at ${reorder} ${ingredient?.unit || ''}`,
                            onClick: () => navigateToInventory('stock_list'),
                        });
                    }
                }
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'remake_requests', filter: `branch_id=eq.${staffBranchId}` },
                (payload) => {
                    fetchRemakeAlerts(staffBranchId, staffBranchRole);
                    if (staffBranchRole !== 'Admin') return;
                    if (payload.eventType !== 'INSERT') return;
                    const row = payload.new;
                    const submitter = row.submitted_by_barista
                        ? `${row.submitted_by_barista.first_name} ${row.submitted_by_barista.last_name}`
                        : 'A barista';
                    const responsible = (row.responsible_staff || []).map(s => s.name).join(', ');
                    pushToast({
                        type: 'remake',
                        title: `${submitter} requested a remake`,
                        message: `Order #${row.reference_number || row.order_id} · ${row.reason}${responsible ? ` · Responsible: ${responsible}` : ''}`,
                        onClick: () => navigateToInventory('remake_activity'),
                    });
                }
            )
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'eod_report_log', filter: `branch_id=eq.${staffBranchId}` },                (payload) => {
                    fetchEODAlerts(staffBranchId, staffBranchRole);
                    const row = payload.new;
                    pushToast({
                        type: 'eod',
                        status: row.status,
                        title: row.status === 'sent' ? 'EOD report sent' : 'EOD report failed to send',
                        message: `${row.branch_name || 'Branch'} · ${row.report_date}${row.status === 'failed' && row.error_message ? ` · ${row.error_message}` : ''}`,
                    });
                }
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'inventory', table: 'inventory_adjustment_requests', filter: `branch_id=eq.${staffBranchId}` },
                async (payload) => {
                    fetchBranchAdjustmentRequests(staffBranchId, staffBranchRole);
                    if (payload.eventType === 'INSERT') {
                        const row = payload.new;
                        const [{ data: ingredient }, { data: barista }] = await Promise.all([
                            supabase.schema('inventory').from('ingredients')
                                .select('name, unit').eq('ingredient_id', row.ingredient_id).single(),
                            row.requested_by_barista_id
                                ? supabase.from('barista_info')
                                    .select('first_name, last_name').eq('barista_id', row.requested_by_barista_id).single()
                                : Promise.resolve({ data: null }),
                        ]);
                        const requesterName = barista ? `${barista.first_name} ${barista.last_name}` : 'A staff member';
                        pushToast({
                            type: 'adjustment',
                            title: `${requesterName} requested a stock ${row.adjustment_type === 'add' ? 'addition' : 'deduction'}`,
                            message: `${ingredient?.name || 'Ingredient'} · ${row.quantity} ${ingredient?.unit || ''} · ${row.reason}`,
                            onClick: () => navigateToInventory('adjustment_requests'),
                        });
                    }
                    if (
                        payload.eventType === 'UPDATE' &&
                        staffBranchRole === 'Barista' &&
                        selectedBarista?.barista_id &&
                        String(payload.new.requested_by_barista_id) === String(selectedBarista.barista_id) &&
                        (payload.new.status === 'approved' || payload.new.status === 'rejected') &&
                        payload.old?.status !== payload.new.status
                    ) {
                        fetchAdjustmentResolutions(staffBranchId, staffBranchRole, selectedBarista.barista_id);
                        const row = payload.new;
                        const { data: ingredient } = await supabase
                            .schema('inventory').from('ingredients')
                            .select('name, unit').eq('ingredient_id', row.ingredient_id).single();
                        const approved = row.status === 'approved';
                        pushToast({
                            type: 'eod',
                            status: approved ? 'sent' : 'failed',
                            title: approved ? 'Your adjustment request was approved' : 'Your adjustment request was rejected',
                            message: `${ingredient?.name || 'Ingredient'} · ${row.quantity} ${ingredient?.unit || ''} · ${row.adjustment_type === 'add' ? '+ Add' : '− Deduct'}`,
                            onClick: () => navigateToInventory('adjustment_requests'),
                        });
                    }
                }
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'inventory', table: 'staff_counts', filter: `branch_id=eq.${staffBranchId}` },
                async (payload) => {
                    fetchStockCountAlerts(staffBranchId, staffBranchRole);
                    if (payload.eventType === 'INSERT' && payload.new.counted_by_barista_id) {
                        const row = payload.new;
                        const [{ data: ingredient }, { data: barista }] = await Promise.all([
                            supabase.schema('inventory').from('ingredients')
                                .select('name, unit').eq('ingredient_id', row.ingredient_id).single(),
                            supabase.from('barista_info')
                                .select('first_name, last_name').eq('barista_id', row.counted_by_barista_id).single(),
                        ]);
                        const baristaName = barista ? `${barista.first_name} ${barista.last_name}` : 'A barista';
                        const typeLabel = row.count_type === 'beginning' ? 'beginning-of-shift'
                            : row.count_type === 'end_of_shift' ? 'end-of-shift' : 'general';
                        pushToast({
                            type: 'count',
                            title: `${baristaName} submitted a ${typeLabel} count`,
                            message: `${ingredient?.name || 'Ingredient'} · counted ${row.counted_quantity} ${ingredient?.unit || ''}`,
                            onClick: () => navigateToInventory('stock_count_review'),
                        });
                    }
                }
            )
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'admin_requests', filter: `branch_id=eq.${staffBranchId}` },
                (payload) => {
                    if (staffBranchRole !== 'Admin') return;
                    const row = payload.new;
                    if (row.status !== 'approved' && row.status !== 'rejected') return;
                    const typeLabel = row.item_type === 'variant' ? 'Variant' : row.item_type === 'modifier' ? 'Modifier' : 'Menu item';
                    pushToast({
                        type: 'eod',
                        status: row.status === 'approved' ? 'sent' : 'failed',
                        title: `${typeLabel} ${row.request_type} request ${row.status}`,
                        message: `"${row.item_name}" was ${row.status} by Super Admin`,
                        onClick: () => navigateToInventory('menu'),
                    });
                }
            )
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'menu_price_change_requests', filter: `branch_id=eq.${staffBranchId}` },
                (payload) => {
                    if (staffBranchRole !== 'Admin') return;
                    const row = payload.new;
                    if (row.status !== 'approved' && row.status !== 'rejected') return;
                    pushToast({
                        type: 'eod',
                        status: row.status === 'approved' ? 'sent' : 'failed',
                        title: `Price change request ${row.status}`,
                        message: `"${row.item_name}" ₱${parseFloat(row.current_price).toFixed(2)} → ₱${parseFloat(row.requested_price).toFixed(2)}`,
                    });
                }
            )
            .subscribe();

        const fallback = setInterval(() => {
            fetchBranchAdjustmentRequests(staffBranchId, staffBranchRole);
            fetchStockCountAlerts(staffBranchId, staffBranchRole);
            fetchEODAlerts(staffBranchId, staffBranchRole);
            fetchNewOrders(staffBranchId, staffBranchRole);
            fetchLowStockAlerts(staffBranchId, staffBranchRole);
            fetchAdjustmentResolutions(staffBranchId, staffBranchRole, selectedBarista?.barista_id);
            fetchRemakeAlerts(staffBranchId, staffBranchRole);
        }, 60_000);

        return () => {
            supabase.removeChannel(channel);
            clearInterval(fallback);
        };
    }, [staffBranchRole, staffBranchId, selectedBarista, fetchBranchAdjustmentRequests, fetchStockCountAlerts, fetchEODAlerts, fetchNewOrders, fetchLowStockAlerts, fetchAdjustmentResolutions, fetchRemakeAlerts, navigateToInventory, navigateToOrders, navigateToShiftAttendance]);    const value = {
        staffAdjustmentRequests,
        staffAdjustmentResolutions,
        staffCountAlerts,
        staffEODAlerts,
        staffNewOrders,
        staffRemakeAlerts,
        adminLowStockAlerts,
        dismissStockCountAlert,
        dismissAdjustmentNotif,
        dismissAdjustmentResolution,
        dismissEODAlert,
        dismissNewOrderNotif,
        dismissLowStockAlert,
        clearAllStockCountAlerts,
        clearAllEODAlerts,
        clearAllAdjustmentRequests,
        clearAllAdjustmentResolutions,
        clearAllNewOrderNotifs,
        clearAllLowStockAlerts,
        dismissRemakeAlert,
        clearAllRemakeAlerts,
       navigateToInventory,
        navigateToMenuManagement,
        navigateToOrders,
        navigateToShiftAttendance,
    };
    return (
        <NotificationsContext.Provider value={value}>
            {children}
            <ToastStack toasts={toasts} onDismiss={dismissToast} />
        </NotificationsContext.Provider>
    );
}

function ToastStack({ toasts, onDismiss }) {
    if (toasts.length === 0) return null;
    return (
        <div className="fixed top-4 right-4 z-[200] flex flex-col gap-2 w-[340px] max-w-[90vw] pointer-events-none">
            {toasts.map(toast => (
                <div
                    key={toast.id}
                    className="relative pointer-events-auto bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
                    style={{ animation: 'toastSlideIn 0.25s ease-out' }}
                >
                    <button
                        onClick={() => { toast.onClick?.(); onDismiss(toast.id); }}
                        className="w-full text-left px-4 py-3 pr-8 hover:bg-gray-50 transition-colors flex items-start gap-3"
                    >
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${toast.type === 'adjustment' ? 'bg-blue-100' : toast.type === 'eod' ? (toast.status === 'sent' ? 'bg-green-100' : 'bg-red-100') : toast.type === 'neworder' ? 'bg-orange-100' : toast.type === 'remake' ? 'bg-amber-100' : toast.type === 'lowstock' ? 'bg-red-100' : 'bg-purple-100'}`}>
                            {toast.type === 'remake' ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 12a9 9 0 1 1-3.5-7.1M21 3v6h-6" />
                                </svg>
                            ) : toast.type === 'neworder' ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                    <path fill="#EA580C" d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2M1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96C5 16.1 5.9 17 7 17h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1.003 1.003 0 0 0 20 4H5.21l-.94-2z"/>
                                </svg>
                            ) : toast.type === 'lowstock' ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                    <path fill="#DC2626" d="M1 21h22L12 2zm12-3h-2v-2h2zm0-4h-2v-4h2z"/>
                                </svg>
                            ) : toast.type === 'adjustment' ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                    <path fill="#1D4ED8" d="m14.06 9l.94.94L5.92 19H5v-.92zm3.6-6c-.25 0-.51.1-.7.29l-1.83 1.83l3.75 3.75l1.83-1.83c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z"/>
                                </svg>
                            ) : toast.type === 'eod' ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                    <path fill={toast.status === 'sent' ? '#16A34A' : '#DC2626'} d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2m0 4l-8 5l-8-5V6l8 5l8-5z"/>
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                    <path fill="#7C3AED" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6m7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/>
                                </svg>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 leading-snug">{toast.title}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{toast.message}</p>
                        </div>
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDismiss(toast.id); }}
                        className="absolute top-2 right-2 text-gray-300 hover:text-gray-500 p-1"
                        title="Dismiss"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                    <div
                         className={`h-0.5 ${toast.type === 'adjustment' ? 'bg-blue-400' : toast.type === 'neworder' ? 'bg-orange-400' : toast.type === 'remake' ? 'bg-amber-400' : toast.type === 'lowstock' ? 'bg-red-400' : 'bg-purple-400'}`}
                        style={{ animation: 'toastShrink 6s linear forwards' }}
                    />
                </div>
            ))}
        </div>
    );
}